import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./DateRangeFilter.css";
import { subDays, subMonths, subYears, startOfDay, endOfDay } from "date-fns";
import { FaCalendarAlt } from "react-icons/fa";

const shortcuts = [
    { label: "Today", getValue: () => [startOfDay(new Date()), endOfDay(new Date())] },
    { label: "Yesterday", getValue: () => [startOfDay(subDays(new Date(), 1)), endOfDay(subDays(new Date(), 1))] },
    { label: "Past Week", getValue: () => [subDays(new Date(), 7), new Date()] },
    { label: "Past Month", getValue: () => [subMonths(new Date(), 1), new Date()] },
    { label: "Past 6 Months", getValue: () => [subMonths(new Date(), 6), new Date()] },
    { label: "Past 1 Year", getValue: () => [subYears(new Date(), 1), new Date()] },
];

const DateRangeFilter = ({ startDate, endDate, onChange, placeholderText = "Select Date Range" }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Helper to check if a shortcut is currently selected
    const isShortcutActive = (getValue) => {
        const [start, end] = getValue();
        if (!startDate || !endDate) return false;

        // Simple comparison of timestamps or strings
        // Note: react-datepicker returns Date objects
        return (
            start.toDateString() === startDate.toDateString() &&
            end.toDateString() === endDate.toDateString()
        );
    };

    const handleShortcutClick = (getValue) => {
        const [start, end] = getValue();
        onChange([start, end]);
        setIsOpen(false);
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        onChange([start, end]);
        // Only close if both dates are selected
        // if (start && end) setIsOpen(false); 
    };

    // Custom Input Component to trigger the calendar
    const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
        <div
            className="custom-date-input"
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0.375rem 0.75rem",
                border: "1px solid #dee2e6",
                borderRadius: "0.375rem",
                cursor: "pointer",
                backgroundColor: "#fff",
                minHeight: "38px",
            }}
            onClick={onClick}
            ref={ref}>
            {startDate && endDate
                ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                : <span style={{ color: "#9E9E9E" }}>{placeholderText}</span>}
            <FaCalendarAlt color="#dee2e6" />
        </div>
    ));

    return (
        <div className="date-range-filter-wrapper">
            <DatePicker
                style={{ width: "100%", display: "block" }}
                selected={startDate}
                onChange={handleDateChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                customInput={<CustomInput />}
                open={isOpen}
                onInputClick={() => setIsOpen(true)}
                onClickOutside={() => setIsOpen(false)}
                checkCloser={true}
                shouldCloseOnSelect={false} // Keep open to allow seeing the range or clicking shortcuts
                calendarContainer={({ className, children }) => (
                    <div className={className + " custom-calendar-container"}>
                        <div className="shortcuts-sidebar">
                            <div className="shortcuts-scroll">
                                {shortcuts.map((shortcut) => (
                                    <div
                                        key={shortcut.label}
                                        className={`shortcut-item ${isShortcutActive(shortcut.getValue) ? "active" : ""}`}
                                        onClick={() => handleShortcutClick(shortcut.getValue)}
                                    >
                                        {shortcut.label}
                                    </div>
                                ))}
                            </div>

                            <div
                                className="shortcut-item clear-btn"
                                onClick={() => { onChange([null, null]); setIsOpen(false); }}
                            >
                                Clear
                            </div>
                        </div>

                        <div className="calendar-content">{children}</div>
                    </div>

                )}
            />
        </div>
    );
};

export default DateRangeFilter;
